<script setup lang="ts">
import { fromNow } from '@thecompaniesapi/date-helpers'
import { prompts } from '@thecompaniesapi/shared/client'
import type { PromptFeature, PromptLoadingState } from '@thecompaniesapi/shared/client'

const props = defineProps({
  border: {
    default: true,
    type: Boolean,
  },
  borderConfig: {
    default: undefined,
    type: Object as PropType<{
      colors: string[]
      duration: number
      opacity: number
      spread: number
    }>,
  },
  completable: {
    default: true,
    type: Boolean,
  },
  completions: {
    default: () => [],
    type: Array as PropType<InputCompletion[]>,
  },
  direction: {
    default: 'bottom',
    type: String,
  },
  forceButton: {
    default: false,
    type: Boolean,
  },
  hoverable: {
    default: true,
    type: Boolean,
  },
  isPreview: {
    default: false,
    type: Boolean,
  },
  light: {
    default: false,
    type: Boolean,
  },
  loading: {
    default: false,
    type: Boolean,
  },
  placeholders: {
    default: () => [],
    type: Array as PropType<string[]>,
  },
  showCleanup: {
    default: true,
    type: Boolean,
  },
  showContentOnLoading: {
    default: false,
    type: Boolean,
  },
  stealFocus: {
    default: true,
    type: Boolean,
  },
  submittable: {
    default: false,
    type: Boolean,
  },
})

const emit = defineEmits<{
  submit: []
}>()

const promptStore = usePromptStore()
const enrichmentStore = useEnrichmentStore()
const { t } = useI18n()

// Light models
const promptModel = defineModel<string>({ default: '' })
const completionModel = defineModel<InputCompletion>('completion')
const isFileUploadHovered = defineModel<boolean>('isFileUploadHovered', { default: false })
const disabled = defineModel<boolean>('disabled', { default: true })
const loadingState = defineModel<PromptLoadingState | undefined>('loadingState', { default: undefined })
const localLoadingStateIndex = computed(() => {
  return loadingState.value
    ? promptStore.loadingStates.findIndex(state => state === loadingState.value)
    : undefined
})

const dropzoneRef = useTemplateRef<HTMLDivElement>('dropzoneRef')

enrichmentStore.bindDropzoneRef(dropzoneRef)

const borderConfig = computed(() => {
  if (!props.border) {
    return {
      colors: [],
      duration: 0,
      hoverable: false,
      opacity: 0,
      spread: 0,
    }
  }

  if (props.light) {
    return {
      duration: props?.loading ? 2000 : 6000,
      hoverable: props.hoverable,
      opacity: props?.loading ? 0.8 : 0.4,
      spread: props?.loading ? 10 : 6,
    }
  }

  return {
    duration: promptStore.currentAction || enrichmentStore.isDroppingFile || promptStore.loading ? 2000 : 6000,
    hoverable: props.hoverable,
    opacity: !!promptStore.currentAction || enrichmentStore.isDroppingFile || promptStore.loading ? 0.8 : 0.6,
    spread: !!promptStore.currentAction || enrichmentStore.isDroppingFile || promptStore.loading ? 16 : 8,
  }
})

function handleUploadButton(): void {
  if (enrichmentStore.fileInputRef && !promptModel.value && !completionModel.value) {
    enrichmentStore.fileInputRef.click()
  }
}

// Make prompt writeable only once mounted
onMounted(() => (disabled.value = false))
</script>

<template>
  <div
    ref="dropzoneRef"
    class="relative flex w-full items-center"
    :class="{ 'pointer-events-none select-none': isPreview }"
  >
    <AnimatedBorder
      class="w-full"
      :duration="borderConfig.duration"
      :hoverable="hoverable"
      :opacity="borderConfig.opacity"
      :spread="borderConfig.spread"
    >
      <AnimatedInput
        v-if="!light"
        v-model="promptStore.prompt"
        v-model:completion="promptStore.selectedCompletion"
        v-model:completions-force="promptStore.completionsForce"
        v-model:focused="promptStore.isFocused"
        v-model:selected-index="promptStore.selectedIndex"
        :can-submit="!!promptStore.currentAction"
        :cleanup-tooltip="t('prompt.cleanup')"
        :completable="completable"
        :completions="promptStore.completions"
        :completions-direction="promptStore.promptContext === 'landing' ? 'bottom' : 'top'"
        :disabled="disabled"
        :force-cleanup="!!promptStore.currentAction"
        :loading="promptStore.submitting || !!promptStore.loading || !!loading"
        :placeholders="props?.placeholders?.length ? placeholders : [t('prompt.placeholders.query1'), t('prompt.placeholders.query2'), t('prompt.placeholders.query3')]"
        :run="!!promptStore.currentAction"
        :show-content="loading || !!promptStore?.loading || enrichmentStore?.isDroppingFile || enrichmentStore?.isSelectingFile || !!promptStore?.currentAction || isFileUploadHovered"
        :steal-focus="stealFocus"
        :submit-tooltip="promptStore.currentAction ? t('prompt.run') : t('prompt.submit')"
        @blur="promptStore.isFocused = false"
        @cleanup="promptStore.reset"
        @focus="promptStore.isFocused = true"
        @reset="promptStore.reset"
        @submit="promptStore.process"
      >
        <template #completion-item="{ completion, selectCompletion }">
          <div
            v-if="completion.key && prompts.features.includes(completion.key as PromptFeature)"
            class="grid grid-cols-[4rem,1fr] items-center gap-3"
            @click="selectCompletion(completion)"
          >
            <div>
              <UButton
                :color="completion.color"
                size="2xs"
                tabindex="-1"
                variant="subtle"
              >
                @{{ completion.label }}
              </UButton>
            </div>

            <div class="truncate text-left opacity-95">
              <span>{{ completion.description }}</span>
            </div>
          </div>

          <div
            v-if="completion.key.startsWith('history:')"
            class="grid grid-cols-[4rem,1fr,auto] items-center gap-3"
            @click="selectCompletion(completion)"
          >
            <div>
              <UButton
                :color="completion.color"
                size="2xs"
                tabindex="-1"
                variant="subtle"
              >
                @{{ completion?.feature }}
              </UButton>
            </div>

            <div class="truncate text-left opacity-95">
              <span>{{ completion.description }}</span>
            </div>

            <div class="flex items-center justify-center gap-1">
              <UButton
                v-if="completion?.createdAt || completion?.updatedAt"
                :label="fromNow(completion?.createdAt || completion?.updatedAt)"
                size="2xs"
                trailing
                :ui="{ padding: { '2xs': 'px-0' } }"
                variant="link"
                @click.stop="selectCompletion(completion)"
              />

              <UButton
                v-if="!isPreview"
                :icon="getIcon('trash')"
                size="2xs"
                trailing
                :ui="{ padding: { '2xs': 'px-0' } }"
                variant="link"
                @click.stop="promptStore.removeFromHistory(completion.key)"
              />
            </div>
          </div>
        </template>

        <template #prefix="{ completion }">
          <div v-if="completion">
            <UButton
              v-if="prompts.features.includes(completion.key as PromptFeature)"
              class="inline-flex shrink-0"
              :color="completion.color"
              size="2xs"
              variant="subtle"
            >
              @{{ completion.label }}
            </UButton>
          </div>
        </template>

        <template #content>
          <div
            v-if="enrichmentStore.isDroppingFile || enrichmentStore.isSelectingFile || isFileUploadHovered"
            class="size-full"
          >
            <div class="flex size-full items-center text-gray-500">
              {{ t('dropzone.description') }}
            </div>
          </div>

          <div
            v-if="loading || !!promptStore.loading"
            class="size-full"
          >
            <div class="flex size-full items-center text-gray-500">
              <AnimationRotating
                v-model="promptStore.loadingStateIndex"
                direction="horizontal"
                :interval="0"
                :slots="promptStore.loadingStates"
              >
                <template
                  v-for="state in promptStore.loadingStates"
                  :key="state"
                  #[state]
                >
                  <AnimateRadiantText class="w-full truncate text-left font-normal sm:text-base">
                    {{ t(`prompt.loading.${state}`) || t('prompt.loading.loading') }}
                  </AnimateRadiantText>
                </template>
              </AnimationRotating>
            </div>
          </div>

          <div
            v-if="promptStore.currentAction"
            class="size-full"
          >
            <div class="flex size-full items-center gap-1 overflow-x-auto">
              <ActionDisplay
                :action="promptStore.currentAction"
                :count="promptStore.currentResponse?.count"
                request
              />
            </div>
          </div>
        </template>

        <template #button>
          <UTooltip
            :popper="{ placement: 'bottom' }"
            :text="t('prompt.upload')"
            @click="handleUploadButton"
            @mouseenter="isFileUploadHovered = true"
            @mouseleave="isFileUploadHovered = false"
          >
            <div class="transition-base hidden size-8 cursor-pointer items-center justify-center rounded-full bg-transparent opacity-80 hover:opacity-100 sm:flex">
              <UIcon :name="getIcon('attachment')" />
            </div>
          </UTooltip>
        </template>
      </AnimatedInput>

      <AnimatedInput
        v-else
        v-model="promptModel"
        v-model:completion="completionModel"
        :completable="completable"
        :completions="completions"
        :force-button="forceButton"
        :force-submit="submittable"
        :loading="loading"
        :placeholders="props?.placeholders?.length ? placeholders : [t('prompt.placeholders.query1'), t('prompt.placeholders.query2'), t('prompt.placeholders.query3')]"
        :show-cleanup="showCleanup"
        :show-content="loading && showContentOnLoading"
        :steal-focus="stealFocus"
        :submit-tooltip="t('prompt.submit')"
        @submit="emit('submit')"
      >
        <template #prefix="{ completion }">
          <div v-if="completion">
            <UButton
              class="inline-flex shrink-0"
              :color="completion.color"
              size="2xs"
              variant="subtle"
            >
              @{{ completion.label }}
            </UButton>
          </div>
        </template>

        <template
          v-if="$slots.input"
          #input
        >
          <slot name="input" />
        </template>

        <template #button>
          <slot name="button" />
        </template>

        <template #content>
          <div
            v-if="loading && showContentOnLoading"
            class="size-full"
          >
            <div class="flex size-full items-center text-gray-500">
              <AnimationRotating
                v-model="localLoadingStateIndex"
                direction="horizontal"
                :interval="0"
                :slots="promptStore.loadingStates"
              >
                <template
                  v-for="state in promptStore.loadingStates"
                  :key="state"
                  #[state]
                >
                  <AnimateRadiantText class="w-full truncate text-left font-normal sm:text-base">
                    {{ t(`prompt.loading.${state}`) || t('prompt.loading.loading') }}
                  </AnimateRadiantText>
                </template>
              </AnimationRotating>
            </div>
          </div>
        </template>
      </AnimatedInput>
    </AnimatedBorder>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "dropzone": {
      "description": "Drop your file here or click to upload"
    },
    "prompt": {
      "cleanup": "Cleanup",
      "loading": {
        "analyzing-context": "Analyzing context...",
        "fetching-companies": "Retrieving companies...",
        "fetching-context": "Retrieving context...",
        "finding-feature": "Analyzing feature...",
        "finding-segmentation": "Analyzing segmentation...",
        "finding-target": "Analyzing target...",
        "loading": "Thinking...",
        "resolving-company-names": "Resolving company names...",
        "resolving-data-schema": "Resolving data schema...",
        "validating-segmentation": "Validating segmentation criteria..."
      },
      "run": "Run",
      "submit": "Submit",
      "upload": "Enrich a file of companies"
    }
  },
  "es": {
    "dropzone": {
      "description": "Arrastra tu archivo aquí o haz click para subir"
    },
    "prompt": {
      "cleanup": "Limpiar",
      "loading": {
        "analyzing-context": "Analizando contexto...",
        "fetching-companies": "Recuperando empresas...",
        "fetching-context": "Recuperando contexto...",
        "finding-feature": "Analizando funcionalidad...",
        "finding-segmentation": "Analizando segmentación...",
        "finding-target": "Analizando objetivo...",
        "loading": "Pensando...",
        "resolving-company-names": "Resolviendo nombres de empresas...",
        "resolving-data-schema": "Resolviendo esquema de datos...",
        "validating-segmentation": "Validando criterios de segmentación..."
      },
      "run": "Ejecutar",
      "submit": "Enviar",
      "upload": "Enriquecer un archivo de empresas"
    }
  },
  "fr": {
    "dropzone": {
      "description": "Glissez-déposez votre fichier ici ou cliquez pour télécharger"
    },
    "prompt": {
      "cleanup": "Nettoyer",
      "loading": {
        "analyzing-context": "Analyse du contexte...",
        "fetching-companies": "Récupération des entreprises...",
        "fetching-context": "Récupération du contexte...",
        "finding-feature": "Analyse des fonctionnalités...",
        "finding-segmentation": "Analyse de la segmentation...",
        "finding-target": "Analyse de la cible...",
        "loading": "Réflexion en cours...",
        "resolving-company-names": "Résolution des noms d'entreprises...",
        "resolving-data-schema": "Résolution du schéma de données...",
        "validating-segmentation": "Validation des critères de segmentation..."
      },
      "run": "Exécuter",
      "submit": "Envoyer",
      "upload": "Enrichir un fichier d'entreprises"
    }
  }
}
</i18n>
